import { Flex, Box, Text, useToast, PseudoBox } from '@chakra-ui/core'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { CampaignsTable } from './CompetitionsTable'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import useLocaleText from 'components/useLocaleText'
import { AppDispatch } from 'config/redux'
import { getRetailCampaigns } from 'redux/actions/campaigns'
import { useCampaignModals } from './CompetitionModalsManagerContext'
import { RefreshCcw } from 'lucide-react'
import { DateRangeState } from '../commonUI/AdminDatePicker'
import { NavButtonTabs } from 'components/CommonComponents/AudienceTabs'
import dayjs from 'dayjs'
import keys from 'constants/keys'
import { getUTCOffset } from 'utils'
import { CAMPAIGN_STATUS_TYPES } from './utils'

export type TabKey = 'active' | 'inactive'

export const CompetitionsBoardContent: React.FC = () => {
  const toast = useToast()
  const { openCampaignModal } = useCampaignModals()
  const dispatch: AppDispatch = useDispatch()

  const theme = useSelector((state: RootState) => state.general.theme)
  const { isConfigReady } = useSelector((state: RootState) => state.config)
  const { campaigns, loading } = useSelector((state: RootState) => state.campaigns)

  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState<TabKey>('active')
  const [dateRange, setDateRange] = useState<DateRangeState>({
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
  })

  const t_competitions = useLocaleText('competitions', true)
  const create_new_competition = useLocaleText('create_competition', true)
  const t_active = useLocaleText('active', true)
  const t_inactive = useLocaleText('inactive', true)

  const handleToggle = (newStatus: TabKey) => {
    setStatus(newStatus)
  }

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      if (status === 'active') {
        return campaign.status === CAMPAIGN_STATUS_TYPES.ACTIVE
      }
      return campaign.status === CAMPAIGN_STATUS_TYPES.DEACTIVE
    })
  }, [campaigns, status])

  const loadCompetitions = useCallback(
    async (newDateRange?: DateRangeState) => {
      setIsLoading(true)
      const { startDate, endDate } = newDateRange || dateRange

      const from_ts = dayjs(startDate).format(keys.DATE_FORMAT_API)
      const to_ts = dayjs(endDate).format(keys.DATE_FORMAT_API)
      try {
        await dispatch(
          getRetailCampaigns({
            from_ts: Number(from_ts),
            to_ts: Number(to_ts),
            utc_offset: getUTCOffset(),
            campaign_type: 1,
          })
        )
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to get competitions',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        })
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, toast]
  )

  const handleDateRangeChange = useCallback(
    async (newDateRange: DateRangeState) => {
      setDateRange(newDateRange)
      await loadCompetitions(newDateRange)
    },
    [loadCompetitions]
  )

  useEffect(() => {
    if (isConfigReady) {
      loadCompetitions()
    }
  }, [isConfigReady, loadCompetitions])

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Flex align="center" style={{ gap: '10px' }}>
          <Text fontSize="2xl" fontWeight="bold">
            {t_competitions}
          </Text>
          <PseudoBox
            as="button"
            onClick={() => loadCompetitions()}
            color={theme?.elementsColor}
            _hover={{ cursor: 'pointer' }}
          >
            <RefreshCcw size={20} />
          </PseudoBox>
        </Flex>
        <NoFocusButton
          w="max-content"
          maxWidth="200px"
          bg={theme?.elementsColor}
          color="white"
          rounded="md"
          background={theme?.elementsColor}
          onClick={() => openCampaignModal()}
          style={{ gap: '5px' }}
          _hover={{ opacity: 0.8 }}
        >
          <Text fontSize="14px">{create_new_competition}</Text>
        </NoFocusButton>
      </Flex>

      <Box w="100%" mb={6} maxWidth={{ base: '100%', lg: '400px' }}>
        <NavButtonTabs
          value={status}
          onChange={(val) => handleToggle(val as TabKey)}
          tabs={[
            { key: 'active', label: t_active },
            { key: 'inactive', label: t_inactive },
          ]}
        />
      </Box>

      <CampaignsTable
        campaigns={filteredCampaigns}
        loading={isLoading || loading}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
      />
    </Box>
  )
}
